/**
 * Système centralisé de z-index pour garantir une hiérarchie cohérente
 * dans toute l'application.
 *
 * Hiérarchie (du plus bas au plus haut):
 * - Base elements (1-10): Éléments de base (grid items, carousels, etc.)
 * - Navbar/Header (100): Navigation principale
 * - Dropdowns/Tooltips (1000-2000): Menus déroulants, tooltips, selectors
 * - Fullscreen modes (3000): Modes plein écran (dashboard fullscreen)
 * - Fullscreen buttons (3001): Boutons dans fullscreen
 * - Data Chat (8998): Chat de données (en dessous des drawers et modales)
 * - Drawers/Sidebars (9000): Panneaux latéraux (au-dessus du fullscreen et du chat)
 * - Modals/Dialogs standard (10000): Modales standard
 * - Popovers dans modales (10001): Popovers qui doivent toujours être au-dessus des modales
 * - Filter Drawer (10003): Drawer de filtres (priorité maximale)
 * - Color Picker (10010): Sélecteur de couleur (priorité maximale, au-dessus de toutes les modales)
 *
 * Exemples d'utilisation:
 * - Pour un dropdown: className={zIndexClass(Z_INDEX.DROPDOWN)}
 * - Pour un style inline: style={zIndexStyle(Z_INDEX.MODAL)}
 * - Pour un backdrop de modale: style={zIndexStyle(Z_INDEX.MODAL_BACKDROP)}
 */

export const Z_INDEX = {
  // Éléments de base (1-10)
  BASE: {
    DEFAULT: 1,
    RESIZING: 1,
    PLACEHOLDER: 2,
    DRAGGING: 3,
    CAROUSEL: 1,
    STEPPER: 1,
    OVERLAY: 10, // Pour les overlays locaux (ex: z-10 dans les composants)
  },

  // Navbar/Header (100)
  NAVBAR: 100,
  GRID_TOOLBOX: 100,

  // Dropdowns/Tooltips (1000-2000)
  DROPDOWN: 1000,
  TOOLTIP: 1000,
  SELECTOR: 1000,
  GEOLOCATION_SELECTOR: 1000,
  MULTI_SELECT: 1000, // Utiliser la même valeur que DROPDOWN pour cohérence
  SEARCH_SUGGESTIONS: 1000, // Utiliser la même valeur que DROPDOWN
  SEARCH_FORM_SUGGESTIONS: 1000, // Utiliser la même valeur que DROPDOWN
  POPUP: 1000, // Pour les popups génériques

  // Fullscreen modes (3000)
  FULLSCREEN: 3000,
  FULLSCREEN_BUTTON: 3001,

  // Data Chat (8998) - En dessous des drawers et modales
  DATA_CHAT: 8998,

  // Drawers/Sidebars (9000) - Au-dessus du fullscreen et du chat
  DRAWER: 9000,
  DRAWER_BACKDROP: 8999, // Backdrop des drawers (juste en dessous)
  SIDEBAR: 100, // Sidebar locked (en dessous des modales)
  SIDEBAR_OVERLAY: 10002, // Sidebar en mode overlay/hover (au-dessus des modales)
  SIDEBAR_CONTEXT_MENU: 10003, // Menu contextuel de la sidebar (au-dessus de la sidebar overlay)

  // Modals/Dialogs (10000+)
  MODAL: 10000,
  MODAL_BACKDROP: 9999, // Backdrop des modales (juste en dessous de la modale)
  MODAL_PRIORITY: 10001, // Pour les modales qui doivent être au-dessus des autres modales

  // Popovers dans modales (10001) - Toujours au-dessus des modales
  POPOVER: 10001,
  POPOVER_IN_MODAL: 10001, // Alias pour clarifier l'usage

  // Filter Drawer (10003) - Priorité maximale
  FILTER_DRAWER: 10003,

  // Color Picker (10010) - Priorité maximale, au-dessus de toutes les modales
  COLOR_PICKER: 10010,

  // Toast/Notifications (10005) - Pour les notifications futures
  TOAST: 10005,
} as const;

/**
 * Helper pour générer des classes Tailwind avec z-index
 */
export const zIndexClass = (value: number): string => {
  return `z-[${value}]`;
};

/**
 * Helper pour générer des styles inline avec z-index
 */
export const zIndexStyle = (value: number): { zIndex: number } => {
  return { zIndex: value };
};
